import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  EXTENSION_START,
  deriveStatus,
  followupArmTables,
  type FollowupArm,
} from '@/lib/followups-shared';

// Total due+overdue follow-ups for the logged-in user across both arms, using
// the same scope as the /followups worklist: the PI sees every complete case,
// every other clinician sees only cases whose index they recorded. Returns 0
// while loading or on error (a nav badge must never block the header).
export function useFollowupDueCount(): number {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!user) { setCount(0); return; }

    const run = async () => {
      const { data: profile } = await supabase
        .from('profiles').select('id,is_pi').eq('user_id', user.id).single();
      if (!profile) return;

      const perArm = await Promise.all(
        (Object.keys(followupArmTables) as FollowupArm[]).map(async (arm) => {
          const t = followupArmTables[arm];

          let ownIds: string[] | null = null;
          if (!profile.is_pi) {
            const { data: resp } = await supabase
              .from(t.clinicianResponses).select('case_id').eq('clinician_id', profile.id);
            ownIds = Array.from(new Set((resp ?? []).map((r) => r.case_id)));
            if (ownIds.length === 0) return 0;
          }

          let q = (supabase.from(t.cases) as any)
            .select('id,created_at')
            .eq('status', 'complete')
            .gte('created_at', EXTENSION_START);
          if (ownIds) q = q.in('id', ownIds);
          const { data: cases, error } = await q;
          if (error || !cases?.length) return 0;

          const ids = cases.map((c: { id: string }) => c.id);
          const { data: fus } = await supabase
            .from(t.followups).select('case_id').in('case_id', ids);
          const done = new Set((fus ?? []).map((f) => f.case_id));

          return cases.reduce((n: number, c: { id: string; created_at: string }) => {
            const s = deriveStatus(c.created_at, done.has(c.id));
            return n + (s === 'due' || s === 'overdue' ? 1 : 0);
          }, 0);
        }),
      );

      if (!cancelled) setCount(perArm.reduce((a, b) => a + b, 0));
    };

    run().catch((e) => console.error('[useFollowupDueCount]', e));
    return () => { cancelled = true; };
  }, [user]);

  return count;
}
